import React from 'react';
import reasonLogo from '../assets/brand/reason-logo.png';

/**
 * ReasonDMS Brand Header Component
 * Displays the official Reason logo and product name
 */
export const BrandHeader = ({ 
  size = 'default', 
  showTagline = false,
  showSubtitle = false,
  className = '' 
}) => {
  const sizes = {
    small: { logo: 'h-8', text: 'text-lg', tagline: 'text-xs' },
    default: { logo: 'h-10', text: 'text-xl', tagline: 'text-sm' },
    large: { logo: 'h-16', text: 'text-3xl', tagline: 'text-base' },
    xlarge: { logo: 'h-20', text: 'text-4xl', tagline: 'text-lg' }
  };

  const currentSize = sizes[size] || sizes.default;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <img 
        src={reasonLogo} 
        alt="Reason Logo" 
        className={`${currentSize.logo} w-auto object-contain`}
        data-testid="reason-logo"
      />
      <div className="flex flex-col">
        <span className={`font-bold text-slate-900 ${currentSize.text}`} style={{ color: '#1e3a5f' }}>
          ReasonDMS
        </span>
        {showSubtitle && (
          <span className="text-xs text-slate-500">
            Secure Document Intelligence
          </span>
        )}
        {showTagline && (
          <span className={`${currentSize.tagline}`} style={{ color: '#e85d04' }}>
            Secure Documents. Intelligent Decisions
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * ReasonDMS Brand Logo Only Component
 * Displays just the logo without text
 */
export const BrandLogo = ({ size = 'default', className = '' }) => {
  const sizes = {
    tiny: 'h-6',
    small: 'h-8',
    default: 'h-10',
    large: 'h-16',
    xlarge: 'h-24'
  };

  return (
    <img 
      src={reasonLogo} 
      alt="Reason Logo" 
      className={`${sizes[size] || sizes.default} w-auto object-contain ${className}`}
      data-testid="reason-logo-only"
    />
  );
};

/**
 * ReasonDMS Brand Footer Component
 * Professional footer with Made in India branding
 */
export const BrandFooter = ({ className = '', variant = 'default' }) => {
  const isMinimal = variant === 'minimal';
  
  return (
    <footer 
      className={`${isMinimal ? 'bg-slate-50 border-t border-slate-200' : 'bg-slate-900 text-white'} py-4 px-6 ${className}`}
      data-testid="brand-footer"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Company Info */}
          <div className="flex items-center gap-3">
            <img 
              src={reasonLogo} 
              alt="Reason Logo" 
              className="h-8 w-auto object-contain"
            />
            <div className="text-sm">
              <span className={`font-semibold ${isMinimal ? '' : 'text-white'}`} style={{ color: isMinimal ? '#1e3a5f' : undefined }}>
                ReasonDMS
              </span>
              <span className={isMinimal ? 'text-slate-500' : 'text-slate-400'}> – AI-Powered Document Intelligence</span>
            </div>
          </div>
          
          {/* Made in India Badges */}
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <span className={`text-sm font-semibold ${isMinimal ? 'text-slate-700' : 'text-white'}`}>
              🇮🇳 Made in India
            </span>
            <span className={isMinimal ? 'text-slate-300' : 'text-slate-600'}>|</span>
            <span className={`text-xs px-2 py-1 rounded-full ${isMinimal ? 'bg-slate-200 text-slate-600' : 'bg-slate-800 text-slate-300'}`}>
              DPIIT Recognized
            </span>
            <span className={`text-xs px-2 py-1 rounded-full ${isMinimal ? 'bg-orange-100 text-orange-700' : 'bg-orange-900/30 text-orange-400'}`}>
              Startup India
            </span>
          </div>
          
          {/* Copyright */}
          <div className={`text-xs ${isMinimal ? 'text-slate-500' : 'text-slate-400'}`}>
            <span>© {new Date().getFullYear()} Reason Analytics Softech Pvt. Ltd.</span>
            <span className="hidden md:inline ml-2">| Version 2.0</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

/**
 * Brand Colors Reference
 * Primary: #1e3a5f (Deep Navy Blue)
 * Accent: #e85d04 (Orange)
 * Secondary: #2a9d8f (Teal)
 * Light: #f8f9fa
 */
export const brandColors = {
  primary: '#1e3a5f',
  accent: '#e85d04',
  secondary: '#2a9d8f',
  light: '#f8f9fa',
  grey: '#6c757d'
};

export default { BrandHeader, BrandLogo, BrandFooter, brandColors };
